import { createFileRoute, Link, Outlet, redirect, useLocation } from '@tanstack/react-router'
import {
  Building2,
  ChevronRight,
  CreditCard,
  Crown,
  LayoutDashboard,
  LogOut,
  Menu,
  Settings,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { supabase } from '@/integrations/supabase/client'

export const Route = createFileRoute('/master')({
  beforeLoad: async ({ context }) => {
    if (context.auth && !context.auth.isLoading) {
      if (!context.auth.user) {
        throw redirect({ to: '/login' })
      }

      // Bloquear acesso se não for SUPERADMIN
      if (context.auth.user.user_metadata?.role !== 'SUPERADMIN') {
        throw redirect({ to: '/app' })
      }
    }
  },
  component: MasterLayout,
})

function MasterLayout() {
  const location = useLocation()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    window.location.href = '/login'
  }

  const menuItems = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/master', exact: true },
    { name: 'Inquilinos', icon: Building2, path: '/master/tenants' },
    { name: 'Planos', icon: CreditCard, path: '/master/plans' },
    { name: 'Configurações', icon: Settings, path: '/master/settings' },
  ]

  return (
    <div className="flex flex-col lg:flex-row h-screen w-full bg-slate-950 text-slate-300 font-sans selection:bg-amber-500/30 overflow-hidden">
      {/* Mobile Topbar */}
      <div className="flex items-center justify-between p-3.5 bg-slate-900 border-b border-slate-800 lg:hidden z-30 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex justify-center items-center shadow-lg shadow-amber-500/20">
            <Crown className="text-white" size={18} />
          </div>
          <div>
            <span className="font-black text-white tracking-tight leading-none block">
              Master HQ
            </span>
            <span className="text-[9px] text-amber-500 uppercase tracking-widest font-bold block mt-0.5">
              AutoWash Pro SaaS
            </span>
          </div>
        </div>
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 rounded-lg bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 transition-colors"
          aria-label="Toggle menu"
        >
          {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Backdrop Overlay for Mobile */}
      {isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-40 lg:hidden transition-opacity"
        />
      )}

      {/* Sidebar: Premium Dark Style */}
      <aside
        className={`
        fixed inset-y-0 left-0 z-50 w-72 bg-slate-900 border-r border-slate-800 flex flex-col transition-transform duration-300 ease-in-out shadow-2xl
        lg:static lg:translate-x-0
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}
      >
        {/* Brand Header */}
        <div className="h-20 flex items-center justify-between px-6 border-b border-slate-800 bg-slate-900/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex justify-center items-center shadow-lg shadow-amber-500/20">
              <Crown className="text-white" size={24} />
            </div>
            <div>
              <h1 className="text-xl font-black text-white tracking-tight">Master HQ</h1>
              <p className="text-[10px] text-amber-500 uppercase tracking-widest font-bold">
                AutoWash Pro SaaS
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="text-slate-400 hover:text-white lg:hidden p-1"
          >
            <X size={18} />
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-6 px-4 space-y-1">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4 px-2">
            Gestão Global
          </div>

          {menuItems.map((item) => {
            const Icon = item.icon
            const isActive = item.exact
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path)

            return (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setIsSidebarOpen(false)}
                className={`group flex items-center justify-between px-3 py-3 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                    : 'text-slate-400 hover:bg-slate-800/80 hover:text-white border border-transparent'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon
                    size={18}
                    className={
                      isActive ? 'text-amber-500' : 'text-slate-500 group-hover:text-slate-300'
                    }
                  />
                  <span className="font-medium">{item.name}</span>
                </div>
                {isActive && <ChevronRight size={16} className="text-amber-500 opacity-50" />}
              </Link>
            )
          })}
        </div>

        {/* User / Logout */}
        <div className="p-4 border-t border-slate-800 bg-slate-900/50">
          <button
            onClick={handleSignOut}
            className="flex w-full items-center gap-3 px-3 py-3 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all border border-transparent hover:border-red-500/20"
          >
            <LogOut size={18} />
            <span className="font-medium">Sair do Master HQ</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col h-full bg-[#0B0F19] relative z-10 overflow-hidden">
        {/* Background Glows for Premium Vibe */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="flex-1 overflow-auto p-4 lg:p-8 relative z-10">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
